import { useTheme } from "@mui/material/styles";
import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ANNIE_DEFAULT_NAME_KEY, useAnnieAvatar } from "../../../hooks/useAnnieAvatar";
import { useIsMobile } from "../../../hooks/useMobile";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import { AnnieStudioBody, AnnieStudioStatus } from "./AnnieStudioBody";
import { AnnieStudioControls } from "./AnnieStudioControls";
import { AnnieStudioStage } from "./AnnieStudioStage";
import { AnnieStudioTitle } from "./AnnieStudioTitle";
import { AnnieStudioTools } from "./AnnieStudioTools";
import { useAnnieNameDraft } from "./useAnnieNameDraft";
import { useAnnieStudioActions } from "./useAnnieStudioActions";
import { useAnnieStudioDraft } from "./useAnnieStudioDraft";

type Props = {
	open: boolean;
	onClose: () => void;
};

/** Annie Studio: design and name your Annie, or turn her off. The off switch applies at once; everything else waits for Save. */
export const AnnieStudioDialog: FC<Props> = ({ open, onClose }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const { isMobile } = useIsMobile();
	const avatar = useAnnieAvatar();
	const { settings, refetch } = avatar;
	const saved = settings === undefined ? undefined : settings.avatar;
	const studio = useAnnieStudioDraft({ open, saved });
	const defaultName = t(ANNIE_DEFAULT_NAME_KEY);
	const name = useAnnieNameDraft({ open, saved: settings === undefined ? undefined : settings.name, defaultName });
	const actions = useAnnieStudioActions(avatar, studio, name, onClose);
	const isBusy = avatar.isSaving || avatar.isResetting;
	// Each avatar write returns the full settings and rolls back to its own snapshot, so writes must not overlap.
	const isAnyWriteBusy = isBusy || avatar.isSettingHidden;
	const isLoaded = settings !== undefined;

	useEffect(() => {
		if (open) refetch();
	}, [open, refetch]);

	const stage = (
		<AnnieStudioStage
			draft={studio.draft}
			name={name.displayName}
			mood={studio.previewMood}
			onMoodChange={studio.setPreviewMood}
			revision={studio.revision}
			moodRevision={studio.moodRevision}
			lastChange={studio.lastChange}
			isHidden={avatar.isHidden}
			compact={isMobile}
		/>
	);
	const controls = (
		<AnnieStudioControls
			studio={studio}
			name={name}
			defaultName={defaultName}
			isHidden={avatar.isHidden}
			onShowChange={actions.showChange}
			isSettingHidden={avatar.isSettingHidden}
			disabled={isBusy}
		/>
	);

	return (
		<>
			<SystemDialog
				open={open}
				onClose={onClose}
				title={<AnnieStudioTitle />}
				body={
					<AnnieStudioBody
						status={isLoaded ? AnnieStudioStatus.Ready : avatar.isError ? AnnieStudioStatus.Error : AnnieStudioStatus.Loading}
						onRetry={refetch}
						stage={stage}
						controls={controls}
						compact={isMobile}
					/>
				}
				dataTestId="annie-studio-dialog"
				maxWidth="md"
				fullWidth
				fullScreen={isMobile}
				zIndex={theme.zIndex.modal + 1}
				// `&&&&` out-ranks SystemDialog's title-then-content padding; the pinned stage carries it instead.
				contentSx={isMobile ? { px: 2, "&&&&": { pt: 0 } } : undefined}
				actionsSx={{ px: 3, pb: 2, pt: 1.5 }}
				actionsStart={
					<AnnieStudioTools
						onSurprise={actions.surprise}
						onReset={actions.reset}
						hasSavedDesign={saved !== null && saved !== undefined}
						surpriseDisabled={!isLoaded || isBusy}
						resetDisabled={!isLoaded || avatar.isSaving || avatar.isSettingHidden || (saved === null && studio.isDefaultDraft)}
						isResetting={avatar.isResetting}
					/>
				}
				cancel={t("cancel")}
				confirm={t("annie.studio.save")}
				confirmButtonVariant="primary"
				onConfirm={actions.save}
				confirmLoading={avatar.isSaving}
				confirmDisabled={!actions.canSave || avatar.isResetting || avatar.isSettingHidden}
				confirmTooltip={isLoaded && !actions.hasChanges ? t("annie.studio.save.unchanged") : undefined}
			/>
			<SystemDialog
				open={actions.isResetConfirmOpen}
				onClose={actions.cancelReset}
				title={t("annie.studio.reset.confirm.title")}
				body={t("annie.studio.reset.confirm.body")}
				confirm={t("annie.studio.reset.confirm.action")}
				cancel={t("annie.studio.reset.confirm.cancel")}
				confirmButtonVariant="delete"
				onConfirm={actions.confirmReset}
				confirmDisabled={isAnyWriteBusy}
				zIndex={theme.zIndex.modal + 2}
				dataTestId="annie-studio-reset-dialog"
			/>
		</>
	);
};
